// src/components/Settings/BillingSettings.tsx

import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/useToast';
import { useAuthStore } from '../../stores/authStore'; // ✅ AGREGAR
import { Save, DollarSign, TrendingUp, Clock } from 'lucide-react';
import { buildApiUrl } from '../../config/api';

export default function ServicePricingSettings() {
  const { isDark } = useTheme();
  const { success, error: showError } = useToast();
  const { getToken } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState<any>({ grouped: {} });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
  try {
    // ✅ CAMBIAR ESTA LÍNEA
    const token = getToken();
    
    console.log('🔑 [PRICING] Token obtenido:', token ? 'SÍ' : 'NO');
    console.log('🔑 [PRICING] Token length:', token?.length);
    
    if (!token) {
      throw new Error('No hay token de autenticación');
    }
    
    const response = await fetch(buildApiUrl('/admin/pricing'), {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    console.log('📡 [PRICING] Status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ [PRICING] Error:', errorData);
      throw new Error(errorData.error || 'Error cargando configuración');
    }

    const data = await response.json();
    console.log('✅ [PRICING] Datos recibidos:', data);
    setConfigs(data);
  } catch (err) {
    console.error('❌ [PRICING] Error completo:', err);
    showError('Error cargando configuración de precios');
  } finally {
    setLoading(false);
  }
};
      

  const handleSave = async (configId: number, value: number, description: string) => {
    setSaving(true);
    try {
      const token = getToken();
       if (!token) {
        throw new Error('No hay token de autenticación');
       }
      const response = await fetch(buildApiUrl(`/admin/pricing/${configId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ config_value: value, description })
      });

      if (!response.ok) throw new Error('Error actualizando precio');

      success('Precio actualizado correctamente');
      loadConfigs();
    } catch (err) {
      console.error('Error:', err);
      showError('Error al actualizar el precio');
    } finally {
      setSaving(false);
    }
  };

  const PriceCard = ({ config, icon: Icon, color }: any) => {
    const [value, setValue] = useState(config.config_value);
    const [description, setDescription] = useState(config.description || '');

    return (
      <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Nivel {config.level}
              </h4>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {config.config_label}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Precio (USD)
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => setValue(parseFloat(e.target.value))}
              step="0.01"
              className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Descripción
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} resize-none text-sm`}
            />
          </div>

          <button
            onClick={() => handleSave(config.id, value, description)}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Guardar
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-12">Cargando configuración...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Complejidad Técnica */}
      <div>
        <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          💡 Complejidad Técnica
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {configs.grouped.technical_complexity?.map((config: any) => (
            <PriceCard 
              key={config.id} 
              config={config} 
              icon={TrendingUp}
              color="bg-purple-500"
            />
          ))}
        </div>
      </div>

      {/* Nivel de Urgencia */}
      <div>
        <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          ⚡ Nivel de Urgencia
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {configs.grouped.urgency_level?.map((config: any) => (
            <PriceCard 
              key={config.id} 
              config={config} 
              icon={Clock}
              color="bg-orange-500"
            />
          ))}
        </div>
      </div>

      {/* Solución Específica */}
      <div>
        <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          📞 Solución Específica (WhatsApp/Telefónica)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {configs.grouped.specific_solution?.map((config: any) => (
            <PriceCard 
              key={config.id} 
              config={config} 
              icon={DollarSign}
              color="bg-green-500"
            />
          ))}
        </div>
      </div>
    </div>
  );
}